import { useCallback, useEffect, useState } from "react";
import { Badge, Flex, Table } from "@mantine/core";
import styled from "styled-components";
import ScrollBox from "../components/common/Table/ScrollBox";
import Container from "../components/common/Container";
import PageHeader from "../components/common/PageHeader";
import CustomSelect from "../components/common/Inputs/CustomSelect";
import CustomPagination from "../components/common/CustomPagination";
import PlusIcon from "../components/common/Icons/PlusIcon";
import TableDeleteButton from "../components/common/Buttons/TableDeleteButton";
import TableInfoButton from "../components/common/Buttons/TableInfoButton";
import AreYouSureModal from "../components/common/Modals/AreYouSureModal";
import { getDepartmentSelectData } from "../api/departmentApi";
import {
  ShiftTeam,
  deleteShiftTeam,
  getShiftTeams,
} from "../api/shiftRotationApi";
import ShiftTeamFormModal, {
  EMPTY_TEAM_FORM,
  ShiftTeamFormState,
  teamFormOf,
} from "../components/Shifts/ShiftTeamFormModal";
import { createNotification } from "../utils/useNotification";

/**
 * EKİP YÖNETİMİ (plan 2.34) — departmanın içindeki dönüşümlü çalışan kümeler.
 *
 * NEDEN AYRI BİR KAVRAM: departman organizasyon birimi, etiket rol/yetenek
 * paketi — ikisi de ekip değil. Dönen şey departmanın kendisi olamaz, çünkü üç
 * vardiya aynı anda çalışıyor; dönen, departmanın İÇİNDEKİ ekipler.
 *
 * Ekip burada yalnızca AÇILIR. Çevrimdeki sırası "Rotalar" ekranında,
 * personel bağı "Personel Atama" ekranının **Ekip** kipinde kurulur — ikisi de
 * ayrı, çünkü aynı ekip farklı rotalarda farklı sırada olabilir ve "Ahmet ne
 * zamandan beri A Ekibinde" sorusunun tarih aralıklı bir cevabı olmalı.
 *
 * Form ayrı bir sayfa değil modal: ekip kaydının tamamı üç alan (ad, kod,
 * departman) ve İK bunları arka arkaya birkaç kez giriyor — sayfa geçişi
 * gereksiz sürtünme olurdu. Modal `ShiftTeamFormModal`'da; aynı form
 * Departman → Ekipler sekmesinden de açılıyor.
 *
 * Bu sayfa ŞİRKET GENELİ görünüm olarak kaldı: bir departmanın ekipleriyle
 * çalışıyorsan Ayarlar → Departmanlar → (departman) → Ekipler sekmesi daha
 * kısa yol; orada departman zaten seçili gelir.
 */
const PAGE_SIZE = 10;

const ShiftTeams = () => {
  const [teams, setTeams] = useState<ShiftTeam[]>([]);
  const [departmentOptions, setDepartmentOptions] = useState<any[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<ShiftTeamFormState>(EMPTY_TEAM_FORM);

  const [deleteTarget, setDeleteTarget] = useState<ShiftTeam | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const data: any = await getShiftTeams({
        pageNumber: page,
        limit: PAGE_SIZE,
        departmentId: selectedDepartment ?? undefined,
      });
      setTeams(data?.data ?? []);
      setTotalPages(data?.meta?.totalPages ?? 1);
    } catch {
      setTeams([]);
    } finally {
      setLoading(false);
    }
  }, [page, selectedDepartment]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    getDepartmentSelectData()
      .then(({ data }: any) => setDepartmentOptions(data?.data ?? []))
      .catch(() => setDepartmentOptions([]));
  }, []);

  // Departman filtresi değişince ilk sayfaya dön — 3. sayfada süzülünce boş
  // liste görünüp "ekip yok" sanılırdı.
  useEffect(() => {
    setPage(1);
  }, [selectedDepartment]);

  const openCreate = () => {
    // Filtre seçiliyse formda ön dolu gelsin: İK zaten o departmanla çalışıyor.
    setForm({ ...EMPTY_TEAM_FORM, departmentId: selectedDepartment });
    setFormOpen(true);
  };

  const openEdit = (team: ShiftTeam) => {
    setForm(teamFormOf(team));
    setFormOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    const response: any = await deleteShiftTeam(deleteTarget._id);
    createNotification({
      success: response?.success,
      message: response?.success ? "Ekip silindi." : response?.message,
    });
    if (response?.success) await load();
  };

  return (
    <Container>
      <PageHeader
        pageTitle="Ekipler"
        subText="Departmanın içinde dönüşümlü çalışan personel kümeleri. Hangi ekibin hangi hafta hangi vardiyada olduğu Rotalar ekranında kurulur."
        isButtoned
        buttonIcon={<PlusIcon />}
        buttonText="Yeni Ekip"
        buttonOnClick={openCreate}
      />

      <Flex mb={20} gap={16} align="flex-end" wrap="wrap">
        <CustomSelect
          label="Departman"
          placeholder="Tümü"
          data={departmentOptions}
          value={selectedDepartment}
          onChange={setSelectedDepartment}
          clearable
          searchable
          w={240}
        />
      </Flex>

      {!loading && teams.length === 0 ? (
        <EmptyState>
          {selectedDepartment
            ? "Bu departmanda henüz ekip yok."
            : "Henüz ekip tanımlanmamış."}{" "}
          Dönüşümlü vardiya kurmak için önce ekipleri açın (ör.{" "}
          <strong>A Ekibi</strong>, <strong>B Ekibi</strong>), sonra{" "}
          <strong>Rotalar</strong> ekranından çevrimi kurun.
        </EmptyState>
      ) : (
        <TableWrapper>
          <Table style={{ borderCollapse: "separate" }} verticalSpacing="sm">
            <Table.Thead style={{ background: "#EEF2FF" }}>
              <Table.Tr>
                <StyledTh style={{ width: "10%" }}>Kod</StyledTh>
                <StyledTh style={{ width: "32%" }}>Ekip Adı</StyledTh>
                <StyledTh style={{ width: "28%" }}>Departman</StyledTh>
                <StyledTh style={{ width: "18%" }}>Personel</StyledTh>
                <StyledTh style={{ width: "12%" }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {teams.map((team) => (
                <StyledTr key={team._id} onClick={() => openEdit(team)}>
                  <Table.Td>
                    <Badge variant="light" color="indigo">
                      {team.code}
                    </Badge>
                  </Table.Td>
                  <Table.Td>{team.name}</Table.Td>
                  <Table.Td style={{ color: "#4b5563" }}>
                    {team.department?.name ?? "—"}
                  </Table.Td>
                  <Table.Td style={{ color: "#4b5563" }}>
                    {/* Bugün geçerli atamalara göre; geçmişte ekipte olup
                        ayrılmış kişi bu sayıya girmiyor. */}
                    {team.memberCount ?? 0} kişi
                  </Table.Td>
                  <Table.Td>
                    <Flex justify="flex-end">
                      <TableInfoButton
                        onClick={(e: any) => {
                          e.stopPropagation();
                          openEdit(team);
                        }}
                      />
                      <TableDeleteButton
                        onClick={(e: any) => {
                          e.stopPropagation();
                          setDeleteTarget(team);
                          setDeleteOpen(true);
                        }}
                      />
                    </Flex>
                  </Table.Td>
                </StyledTr>
              ))}
            </Table.Tbody>
          </Table>
        </TableWrapper>
      )}

      {totalPages > 1 && (
        <PaginationWrapper>
          <CustomPagination total={totalPages} value={page} onChange={setPage} />
        </PaginationWrapper>
      )}

      <ShiftTeamFormModal
        opened={formOpen}
        onClose={() => setFormOpen(false)}
        form={form}
        setForm={setForm}
        departmentOptions={departmentOptions}
        onSaved={load}
      />

      <AreYouSureModal
        opened={deleteOpen}
        setOpened={setDeleteOpen}
        proccessFunc={handleDelete}
        title="Ekibi Sil"
        text={
          `"${deleteTarget?.name}" ekibini silmek istediğinizden emin misiniz? ` +
          "Ekip bir rotada ya da personel atamasında kullanılıyorsa silme işlemi reddedilir."
        }
        confirmButtonText="Sil"
        cancelButtonText="Vazgeç"
      />
    </Container>
  );
};

export default ShiftTeams;

const TableWrapper = styled(ScrollBox)`
  overflow-y: auto;
  min-height: max-content;
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 6px;
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 16px;
`;

const EmptyState = styled.div`
  border: 1px dashed #d1d5db;
  border-radius: 6px;
  padding: 32px;
  color: #6b7280;
  font-size: 15px;
  line-height: 22px;
`;

const StyledTh = styled(Table.Td)`
  color: #4c4c4c;
  font-size: 14px;
  font-weight: 600;
  line-height: 16px;
`;

const StyledTr = styled(Table.Tr)`
  cursor: pointer;
  color: #111827;
  font-size: 16px;
  font-weight: 400;
  &:hover {
    background: #f1f3f5 !important;
  }
`;
